/**
 * Shared types for the org digest feature.
 *
 * Two digest kinds are delivered on the same plumbing:
 * - operational: the original weekly operational digest (claims, complaints, incidents, controls, risks).
 * - boardRiskCompliance: the Board-level risk & compliance summary (CUR-2504).
 */

export const ORG_DIGEST_TYPE = {
    operational: 'operational',
    boardRiskCompliance: 'boardRiskCompliance',
} as const;

export type OrgDigestType = (typeof ORG_DIGEST_TYPE)[keyof typeof ORG_DIGEST_TYPE];

export const ORG_DIGEST_TYPES: readonly OrgDigestType[] = [
    ORG_DIGEST_TYPE.operational,
    ORG_DIGEST_TYPE.boardRiskCompliance,
];

export const OPERATIONAL_DIGEST_SECTIONS = [
    'claims',
    'complaints',
    'incidents',
    'controls',
    'risks',
    'users',
    'handling-parties',
] as const;

export type OperationalDigestSection = (typeof OPERATIONAL_DIGEST_SECTIONS)[number];

export const BOARD_DIGEST_SECTIONS = [
    'executive-summary',
    'trend-analysis',
    'audit-findings',
    'overdue-backlog',
    'material-issues',
    'top-risks',
    'assurance-emerging',
    'gap-detection',
] as const;

export type BoardDigestSection = (typeof BOARD_DIGEST_SECTIONS)[number];

export const BOARD_DIGEST_SECTION_LABELS: Record<BoardDigestSection, string> = {
    'executive-summary': 'Executive summary',
    'trend-analysis': 'Trend analysis',
    'audit-findings': 'Audit findings',
    'overdue-backlog': 'Overdue actions and backlog',
    'material-issues': 'Material issues',
    'top-risks': 'Top risks and movement',
    'assurance-emerging': 'Assurance outcomes and emerging risks',
    'gap-detection': 'Gap detection',
};

export const OPERATIONAL_DIGEST_SECTION_LABELS: Record<OperationalDigestSection, string> = {
    claims: 'Claims',
    complaints: 'Complaints',
    incidents: 'Incidents',
    controls: 'Controls',
    risks: 'Risks',
    users: 'Users',
    'handling-parties': 'Handling parties',
};

export type DigestRagStatus = 'green' | 'amber' | 'red';
